import { User } from "@coworker/apprestructured/src/shared/types/user";
import { Activity } from "@coworker/apprestructured/src/tasks/types/activity";
import activityTypes from "@coworker/enums/activityTypes";
import { closeTypeKeys } from "@coworker/enums/closeTypes";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import dayjs from "dayjs";

interface DateProps {
  dateTime: string;
  t: (key: string) => string;
}

export const timestampToDate = ({ dateTime, t }: DateProps): string => {
  if (!dateTime) return "";
  const localDate = new Date(dateTime);
  const currentTime = dayjs();
  const timeDifferenceMinutes = currentTime.diff(localDate, "minutes");
  const timeDifferenceHours = currentTime.diff(localDate, "hours");
  const timeDifferenceDays = currentTime.diff(localDate, "days");

  const getTimeString = (
    difference: number,
    singularKey: string,
    pluralKey: string
  ) =>
    `${difference} ${difference === 1 ? t(singularKey) : t(pluralKey)} ${t(
      "agoString"
    )}`;

  if (timeDifferenceMinutes === 0) return t("justNowString");
  if (timeDifferenceMinutes < 60)
    return getTimeString(
      timeDifferenceMinutes,
      "minuteString",
      "minutesString"
    );
  if (timeDifferenceHours < 24)
    return getTimeString(timeDifferenceHours, "hourString", "hoursString");
  if (timeDifferenceDays <= 5)
    return getTimeString(timeDifferenceDays, "dayString", "daysString");

  return localDate.toISOString().split("T")[0]!;
};

export const getUserFullName = (
  userId: string,
  users: User[],
  t: (key: string) => string
): string => {
  const user = users.find((user) => user.uid === userId);
  return user ? user.fullName : t("unknownUserString");
};

function getTeamName(
  teamId: string,
  teams: StoreTeam[],
  t: (key: string) => string
) {
  return (
    teams.find((team) => team.id === teamId)?.name || t("unknownTeamString")
  );
}

export const getHistoryEventDescription = (
  item: Activity,
  users: User[],
  teams: StoreTeam[],
  t: (key: string, params?: Record<string, unknown>) => string
): string => {
  switch (item.event) {
    case activityTypes.CREATE_AND_ASSIGN_TO_TEAM:
      return t("createdAndAssignedTaskToNameString", {
        name: getTeamName(item.assigned_team_id, teams, t),
      });
    case activityTypes.CREATE_AND_ASSIGN_TO_USER:
      return t("createdAndAssignedTaskToNameString", {
        name: getUserFullName(item.assigned_user_id, users, t),
      });
    case activityTypes.PICK_UP:
      return t("pickedUpTheTaskStringCapitalized");
    case activityTypes.REASSIGN:
      return t("reassignedTaskToNameString", {
        name: getUserFullName(item.assigned_user_id, users, t),
      });
    case activityTypes.REASSIGN_TEAM:
      return t("reassignedTaskToNameString", {
        name: getTeamName(item.assigned_team_id, teams, t),
      });
    case activityTypes.COMPLETE:
      return t("completedTheTaskString");
    case activityTypes.DROP:
      return t("droppedTheTaskStringCapitalized");
    case activityTypes.EDIT:
      return t("editedTheTaskStringCapitalized");
    case activityTypes.CLOSE:
      return t("closedTheTaskDueToString", {
        reason: `"${t(closeTypeKeys[item.event_subtype as keyof typeof closeTypeKeys])}"`,
      });
    default:
      return "";
  }
};
